import { useMemo } from 'react';
import { isSharedStation } from '@app/core/stations/helpers';
import { Station } from '@app/core/types/stations';

interface UseSortedFilteredStationsParams {
  readonly stations: readonly Station[];
  readonly recentStationIds: readonly string[];
  readonly favoriteStationIds: readonly string[];
  readonly query: string;
}

interface UseSortedFilteredStationsResult {
  readonly sortedStations: readonly Station[];
  readonly filteredStations: readonly Station[];
  readonly recentStations: readonly Station[];
  readonly sharedStations: readonly Station[];
  readonly favoriteStations: readonly Station[];
  readonly otherStations: readonly Station[];
}

/**
 * Partitions and sorts stations into: recent, favorites, rest, shared (not in recent).
 * Search query is applied to the sorted list; sections keep the same partition rules.
 */
export function useSortedFilteredStations({
  stations,
  recentStationIds,
  favoriteStationIds,
  query,
}: UseSortedFilteredStationsParams): UseSortedFilteredStationsResult {
  const sortedStations = useMemo(() => {
    const recentIdsSet = new Set(recentStationIds);
    const favoriteIdsSet = new Set(favoriteStationIds);
    const recent: Station[] = [];
    const shared: Station[] = [];
    const favorites: Station[] = [];
    const rest: Station[] = [];

    stations.forEach((station) => {
      if (recentIdsSet.has(station.id)) {
        recent.push(station);
      } else if (isSharedStation(station)) {
        shared.push(station);
      } else if (favoriteIdsSet.has(station.id)) {
        favorites.push(station);
      } else {
        rest.push(station);
      }
    });

    const recentSorted = recentStationIds
      .map((id) => recent.find((s) => s.id === id))
      .filter((s): s is Station => s !== undefined);
    const favoritesSorted = favoriteStationIds
      .map((id) => favorites.find((s) => s.id === id))
      .filter((s): s is Station => s !== undefined);

    return [...recentSorted, ...favoritesSorted, ...rest, ...shared];
  }, [stations, recentStationIds, favoriteStationIds]);

  const filteredStations = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) {
      return sortedStations;
    }
    return sortedStations.filter(
      (s) => s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q),
    );
  }, [query, sortedStations]);

  const { recentStations, sharedStations, favoriteStations, otherStations } = useMemo(() => {
    const recentIdsSet = new Set(recentStationIds);
    const favoriteIdsSet = new Set(favoriteStationIds);
    const recent: Station[] = [];
    const shared: Station[] = [];
    const favorites: Station[] = [];
    const other: Station[] = [];

    filteredStations.forEach((station) => {
      if (recentIdsSet.has(station.id)) {
        recent.push(station);
      } else if (isSharedStation(station)) {
        shared.push(station);
      } else if (favoriteIdsSet.has(station.id)) {
        favorites.push(station);
      } else {
        other.push(station);
      }
    });

    return {
      recentStations: recent,
      sharedStations: shared,
      favoriteStations: favorites,
      otherStations: other,
    };
  }, [filteredStations, recentStationIds, favoriteStationIds]);

  return {
    sortedStations,
    filteredStations,
    recentStations,
    sharedStations,
    favoriteStations,
    otherStations,
  };
}
